import {getTestProps} from '@benchling/js-test-utils';
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  createStyles,
  Group,
  Radio,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useContext, useState} from 'react';

import {AskUserResponseStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  AskUserAnswer,
  AskUserDraft,
  AskUserQuestion,
  AskUserQuestionType,
  AskUserReply,
  AskUserResponse,
  AskUserSelectQuestion,
} from 'llm/chat-library/ask-user/AskUserTypes';
import {
  buildAskUserAnswer,
  createEmptyAskUserDraft,
  formatAskUserAnswer,
  formatAskUserResponseMessage,
  getAskUserAnswer,
  getAskUserDraftError,
  isAskUserDraftEmpty,
  MAX_ANSWER_LENGTH,
} from 'llm/chat-library/ask-user/AskUserUtils';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {ChatContext} from 'llm/chat-library/ChatContext';
import {ChatDraftStatus, getOrCreateDraftState, useChatDraftState} from 'llm/chat-library/ChatDraftState';
import {useMCPEnabledServersOptional} from 'llm/mcp-client-library/MCPEnabledServersContext';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const OTHER_VALUE = 'something-else';
const OTHER_LABEL = 'Something else…';
const MULTI_SELECT_HINT = 'Pick any that apply';
const SKIP_TOOLTIP = 'Skip this question, or send a message to tell the agent what to do instead.';
const CHOICE_LINE_HEIGHT = 20;
/** The size of the theme's `md` radio and checkbox. */
const CHOICE_CONTROL_SIZE = 16;

const useStyles = createStyles((theme) => ({
  card: {
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.sm,
    padding: theme.spacing.lg,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[3]}`,
    backgroundColor: theme.white,
  },
  // Every question sits in the same grid cell so the card keeps the size of the largest one and
  // does not jump while the user moves between questions.
  questions: {
    display: 'grid',
  },
  questionSlot: {
    gridArea: '1 / 1',
  },
  hiddenQuestionSlot: {
    visibility: 'hidden',
  },
  questionText: {
    color: theme.colors.gray[9],
    fontSize: theme.fontSizes.xl,
    lineHeight: 1.4,
  },
  // The theme centers checkbox labels, which pushes a label with a description to the middle of the row.
  choiceLabel: {
    alignSelf: 'flex-start',
    fontSize: theme.fontSizes.lg,
    lineHeight: `${CHOICE_LINE_HEIGHT}px`,
    color: theme.colors.gray[9],
  },
  // Centers the control on the first line of its label.
  choiceInner: {
    alignSelf: 'flex-start',
    marginTop: (CHOICE_LINE_HEIGHT - CHOICE_CONTROL_SIZE) / 2,
  },
  choiceControl: {
    borderColor: theme.colors.gray[5],
  },
  otherChoiceLabel: {
    color: theme.colors.gray[6],
  },
  choiceDescription: {
    marginTop: 4,
    fontSize: theme.fontSizes.md,
    color: theme.colors.gray[6],
  },
  stepButton: {
    '&:disabled, &[data-disabled]': {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      color: theme.colors.gray[4],
    },
  },
  submitButton: {
    '&:disabled, &[data-disabled]': {
      backgroundColor: theme.colors.gray[2],
      color: theme.colors.gray[6],
    },
  },
}));

/** Choice values are positional so that no option label can collide with OTHER_VALUE. */
function getOptionValue(index: number): string {
  return `option-${index}`;
}

function getStatusLabel(response: AskUserResponse | null): string {
  if (response == null) {
    return 'You replied in chat';
  }
  return response.status === AskUserResponseStatus.ANSWERED ? 'Answered' : 'Skipped';
}

function AskUserCardFrame({children}: {children: React.ReactNode}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Box className={classes.card} {...getTestProps({component: 'AskUserCard', element: 'root'})}>
      {children}
    </Box>
  );
}

/** The user's answer to one question in the summary, or "Skipped" when they answered only others. */
function SummaryAnswer({
  answer,
  response,
}: {
  answer: AskUserAnswer | undefined;
  response: AskUserResponse | null;
}): JSX.Element | null {
  if (answer != null) {
    return (
      <Text size="sm" weight={600}>
        {formatAskUserAnswer(answer)}
      </Text>
    );
  }
  if (response?.status === AskUserResponseStatus.ANSWERED) {
    return (
      <Text size="sm" color="dimmed" fs="italic">
        Skipped
      </Text>
    );
  }
  return null;
}

/** Lists the questions, with the user's answers when there are any. */
function QuestionSummary({
  questions,
  response,
  status,
}: {
  questions: ReadonlyArray<AskUserQuestion>;
  response: AskUserResponse | null;
  status: string;
}): JSX.Element {
  return (
    <Stack spacing="sm">
      <Text
        size="xs"
        weight={600}
        color="dimmed"
        {...getTestProps({component: 'AskUserCard', element: 'status'})}
      >
        {status}
      </Text>
      {questions.map((question) => (
        <Stack
          key={question.id}
          spacing={2}
          {...getTestProps({component: 'AskUserCard', element: 'summary', key: question.id})}
          data-amp-safe={false}
        >
          <Text size="sm" color="dimmed">
            {question.question}
          </Text>
          <SummaryAnswer
            answer={response != null ? getAskUserAnswer(response, question.id) : undefined}
            response={response}
          />
        </Stack>
      ))}
    </Stack>
  );
}

function SelectQuestionInput({
  question,
  draft,
  labelledBy,
  onChange,
}: {
  question: AskUserSelectQuestion;
  draft: AskUserDraft;
  labelledBy: string;
  onChange: (draft: AskUserDraft) => void;
}): JSX.Element {
  const {classes, cx} = useStyles();
  const isMulti = question.type === AskUserQuestionType.MULTI_SELECT;
  const selectedValues = [
    ...question.options.flatMap((option, index) =>
      draft.selectedLabels.includes(option.label) ? [getOptionValue(index)] : []
    ),
    ...(draft.isOtherSelected ? [OTHER_VALUE] : []),
  ];

  const handleValuesChange = (values: Array<string>): void => {
    onChange({
      ...draft,
      selectedLabels: question.options
        .filter((_option, index) => values.includes(getOptionValue(index)))
        .map((option) => option.label),
      isOtherSelected: values.includes(OTHER_VALUE),
    });
  };

  const choices = [
    ...question.options.map((option, index) => ({
      value: getOptionValue(index),
      label: option.label,
      description: option.description,
      isOther: false,
    })),
    {value: OTHER_VALUE, label: OTHER_LABEL, description: undefined, isOther: true},
  ].map((choice) => {
    const props = {
      value: choice.value,
      label: choice.label,
      description: choice.description,
      classNames: {
        label: cx(classes.choiceLabel, choice.isOther && classes.otherChoiceLabel),
        inner: classes.choiceInner,
        input: classes.choiceControl,
        radio: classes.choiceControl,
        description: classes.choiceDescription,
      },
    };
    return (
      <Box
        key={choice.value}
        {...getTestProps({
          component: 'AskUserCard',
          element: 'option',
          key: choice.isOther ? 'other' : choice.label,
        })}
        data-amp-safe={false}
      >
        {isMulti ? <Checkbox {...props} /> : <Radio {...props} />}
      </Box>
    );
  });

  return (
    <Stack spacing="sm">
      {isMulti ? (
        <Checkbox.Group value={selectedValues} onChange={handleValuesChange} aria-labelledby={labelledBy}>
          <Stack spacing="md">{choices}</Stack>
        </Checkbox.Group>
      ) : (
        <Radio.Group
          value={selectedValues[0] ?? ''}
          onChange={(value) => {
            handleValuesChange([value]);
          }}
          aria-labelledby={labelledBy}
        >
          <Stack spacing="md">{choices}</Stack>
        </Radio.Group>
      )}
      {draft.isOtherSelected ? (
        <TextInput
          aria-label="Something else"
          placeholder="Type your answer"
          maxLength={MAX_ANSWER_LENGTH}
          value={draft.otherText}
          onChange={(event) => {
            onChange({...draft, otherText: event.currentTarget.value});
          }}
          autoFocus={true}
        />
      ) : null}
    </Stack>
  );
}

function QuestionField({
  question,
  isHidden,
  draft,
  onChange,
}: {
  question: AskUserQuestion;
  isHidden: boolean;
  draft: AskUserDraft;
  onChange: (draft: AskUserDraft) => void;
}): JSX.Element {
  const {classes} = useStyles();
  const questionTextId = useId();
  const error = getAskUserDraftError(question, draft);
  return (
    <Stack
      spacing="md"
      aria-hidden={isHidden}
      {...getTestProps({component: 'AskUserCard', element: 'question', key: question.id})}
      data-amp-safe={false}
    >
      <Text weight={600} className={classes.questionText} id={questionTextId}>
        {question.question}
        {question.type === AskUserQuestionType.MULTI_SELECT ? (
          <Text component="span" size="md" weight={400} color="dimmed" ml="xs">
            {MULTI_SELECT_HINT}
          </Text>
        ) : null}
      </Text>
      {question.type === AskUserQuestionType.FREE_TEXT ? (
        <Textarea
          aria-labelledby={questionTextId}
          placeholder={question.placeholder}
          autosize={true}
          minRows={2}
          maxRows={6}
          maxLength={MAX_ANSWER_LENGTH}
          value={draft.text}
          onChange={(event) => {
            onChange({...draft, text: event.currentTarget.value});
          }}
          {...getTestProps({component: 'AskUserCard', element: 'freeText'})}
        />
      ) : (
        <SelectQuestionInput
          question={question}
          draft={draft}
          labelledBy={questionTextId}
          onChange={onChange}
        />
      )}
      {error != null && !isAskUserDraftEmpty(question, draft) ? (
        <Text
          size="xs"
          color="red"
          role="alert"
          {...getTestProps({component: 'AskUserCard', element: 'error'})}
        >
          {error}
        </Text>
      ) : null}
    </Stack>
  );
}

/** The current question's header with back and next arrows and its position, e.g. "2 of 4". */
function QuestionStepper({
  header,
  isSkipped,
  index,
  count,
  onChange,
}: {
  header: string;
  isSkipped: boolean;
  index: number;
  count: number;
  onChange: (index: number) => void;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Group position="apart" noWrap={true}>
      <Group spacing="xs" noWrap={true}>
        <Text
          size="xs"
          weight={600}
          color="dimmed"
          tt="uppercase"
          truncate={true}
          {...getTestProps({component: 'AskUserCard', element: 'header'})}
          data-amp-safe={false}
        >
          {header}
        </Text>
        {isSkipped ? (
          <Text
            size="xs"
            color="dimmed"
            fs="italic"
            {...getTestProps({component: 'AskUserCard', element: 'skipped'})}
          >
            Skipped
          </Text>
        ) : null}
      </Group>
      {count > 1 ? (
        <Group spacing={4} noWrap={true}>
          <ActionIcon
            size="sm"
            variant="subtle"
            aria-label="Previous question"
            className={classes.stepButton}
            disabled={index === 0}
            onClick={() => {
              onChange(index - 1);
            }}
          >
            <BenchlingIcon icon="chevron-left" />
          </ActionIcon>
          <Text size="xs" color="dimmed" {...getTestProps({component: 'AskUserCard', element: 'step'})}>
            {index + 1} of {count}
          </Text>
          <ActionIcon
            size="sm"
            variant="subtle"
            aria-label="Next question"
            className={classes.stepButton}
            disabled={index === count - 1}
            onClick={() => {
              onChange(index + 1);
            }}
          >
            <BenchlingIcon icon="chevron-right" />
          </ActionIcon>
        </Group>
      ) : null}
    </Group>
  );
}

function getRemainingLabel(remainingCount: number): string {
  return remainingCount === 1 ? '1 left to answer' : `${remainingCount} left to answer`;
}

/**
 * The in-chat card for questions the agent asked with the AskUser tool.
 *
 * While the questions are open it shows one question at a time, with arrows to move between them.
 * The user can skip any question; Submit sends the answers to the rest, or a decline when every
 * question was skipped, as the user's next chat message. Once the user has replied, it shows a
 * read-only summary instead.
 */
export default function AskUserCard({
  questions,
  requestEventId,
  reply,
  chatSessionId,
  readonly,
}: {
  questions: ReadonlyArray<AskUserQuestion>;
  /** The assistant message that asks the questions; the response references it and replies to it. */
  requestEventId: string;
  reply: AskUserReply;
  chatSessionId: string;
  /** Hides the inputs and actions, e.g. for a shared or locked chat. */
  readonly: boolean;
}): JSX.Element {
  const {classes, cx} = useStyles();
  const mcpServerIds = useMCPEnabledServersOptional()?.enabledServerIds;
  const beforeSendMessage = useContext(ChatContext)?.beforeSendMessage;
  // The composer's attachments are sent with the answers, and only finished uploads can be sent.
  const [composerDraft] = useChatDraftState(chatSessionId);
  const hasUploadingFiles = composerDraft.message.selectedFiles.some((file) => file.completedUpload == null);
  const [isWaitingForSave, setIsWaitingForSave] = useState(false);
  const [drafts, setDrafts] = useState<ReadonlyMap<string, AskUserDraft>>(new Map());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [skippedIds, setSkippedIds] = useState<ReadonlySet<string>>(new Set());
  // Shown while the reply that carries it is still being sent; cleared if the server rejects it.
  const [submittedResponse, setSubmittedResponse] = useState<AskUserResponse | null>(null);

  if (reply != null) {
    const response = reply.response ?? submittedResponse;
    return (
      <AskUserCardFrame>
        <QuestionSummary questions={questions} response={response} status={getStatusLabel(response)} />
      </AskUserCardFrame>
    );
  }
  if (readonly) {
    return (
      <AskUserCardFrame>
        <QuestionSummary questions={questions} response={null} status="Waiting for an answer" />
      </AskUserCardFrame>
    );
  }

  const draftFor = (question: AskUserQuestion): AskUserDraft =>
    drafts.get(question.id) ?? createEmptyAskUserDraft();
  const remainingCount = questions.filter(
    (question) => !skippedIds.has(question.id) && getAskUserDraftError(question, draftFor(question)) != null
  ).length;
  const isComplete = remainingCount === 0;
  const canSubmit = isComplete && !hasUploadingFiles && !isWaitingForSave;
  const isLastQuestion = currentIndex === questions.length - 1;
  const currentQuestion = questions[currentIndex];
  const isCurrentSkipped = skippedIds.has(currentQuestion.id);

  const updateDraft = (questionId: string, nextDraft: AskUserDraft): void => {
    setDrafts((current) => new Map(current).set(questionId, nextDraft));
    setSkippedIds((current) => {
      const next = new Set(current);
      next.delete(questionId);
      return next;
    });
  };

  const skipCurrentQuestion = (): void => {
    setSkippedIds((current) => new Set(current).add(currentQuestion.id));
    setDrafts((current) => new Map(current).set(currentQuestion.id, createEmptyAskUserDraft()));
    if (!isLastQuestion) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const submit = async (): Promise<void> => {
    if (beforeSendMessage != null) {
      setIsWaitingForSave(true);
      try {
        await beforeSendMessage();
      } finally {
        setIsWaitingForSave(false);
      }
    }
    const answeredQuestions = questions.filter((question) => !skippedIds.has(question.id));
    const response: AskUserResponse =
      answeredQuestions.length > 0
        ? {
            status: AskUserResponseStatus.ANSWERED,
            answers: Object.fromEntries(
              answeredQuestions.map((question) => [
                question.id,
                buildAskUserAnswer(question, draftFor(question)),
              ])
            ),
          }
        : {status: AskUserResponseStatus.DECLINED, answers: {}};
    const {status} = response;
    const summary = formatAskUserResponseMessage(questions, response);
    const draft = getOrCreateDraftState(chatSessionId);
    // Send whatever the user has typed in the composer along with the response, rather than discarding it.
    const typedText = draft.message.text.trim();
    ChatActions.submitDraftMessage(chatSessionId, {
      draft: {
        ...draft,
        status: ChatDraftStatus.DRAFT,
        message: {...draft.message, text: typedText.length > 0 ? `${summary}\n\n${typedText}` : summary},
      },
      createParams: {
        mcpServerIds,
        askUserResponse:
          status === AskUserResponseStatus.ANSWERED
            ? {status, answers: response.answers, requestEventId}
            : {status, requestEventId},
      },
      replyToId: requestEventId,
      // The summary is not the user's text, so only what they typed goes back to the composer.
      restoreMessageOnRejection: draft.message,
      onRejected: () => {
        setSubmittedResponse(null);
      },
    });
    setSubmittedResponse(response);
  };

  return (
    <AskUserCardFrame>
      <Stack spacing="lg">
        <Stack spacing="xs">
          <QuestionStepper
            header={currentQuestion.header}
            isSkipped={isCurrentSkipped}
            index={currentIndex}
            count={questions.length}
            onChange={setCurrentIndex}
          />
          <Box className={classes.questions}>
            {questions.map((question, index) => {
              const isCurrent = index === currentIndex;
              return (
                <Box
                  key={question.id}
                  className={cx(classes.questionSlot, !isCurrent && classes.hiddenQuestionSlot)}
                >
                  <QuestionField
                    question={question}
                    isHidden={!isCurrent}
                    draft={draftFor(question)}
                    onChange={(nextDraft) => {
                      updateDraft(question.id, nextDraft);
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Stack>
        <Group position="apart" noWrap={true}>
          <Tooltip label={SKIP_TOOLTIP} multiline={true} width={240} withArrow={true}>
            <Button
              variant="ghost"
              disabled={isCurrentSkipped}
              onClick={skipCurrentQuestion}
              {...getTestProps({component: 'AskUserCard', element: 'skip'})}
            >
              Skip
            </Button>
          </Tooltip>
          <Group spacing="sm" noWrap={true}>
            {remainingCount > 0 ? (
              <Text
                size="md"
                color="dimmed"
                {...getTestProps({component: 'AskUserCard', element: 'remaining'})}
              >
                {getRemainingLabel(remainingCount)}
              </Text>
            ) : null}
            {isComplete || isLastQuestion ? (
              <Button
                variant="primary"
                className={classes.submitButton}
                disabled={!canSubmit}
                loading={isWaitingForSave}
                onClick={submit}
                {...getTestProps({component: 'AskUserCard', element: 'submit'})}
              >
                Submit
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  setCurrentIndex(currentIndex + 1);
                }}
                {...getTestProps({component: 'AskUserCard', element: 'next'})}
              >
                Next
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </AskUserCardFrame>
  );
}
